import React, { useRef, useEffect, useState, useCallback } from 'react';
import './Player.css';

const PROGRESS_KEY = (videoId) => `hackix_time_${videoId}`;
const RESUME_THRESHOLD = 5;
const SPANISH_DEFAULT = ['coraje el perro cobarde'];

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function parseTime(timeStr) {
  const parts = timeStr.split(':');
  if (parts.length === 3) {
    return parseFloat(parts[0]) * 3600 + parseFloat(parts[1]) * 60 + parseFloat(parts[2]);
  }
  return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
}

function parseVTT(vttText) {
  const cues = [];
  const clean = vttText.replace(/^\s*WEBVTT\s*\n*/i, '');
  const blocks = clean.split(/\n\n+/);
  for (const block of blocks) {
    const lines = block.trim().split('\n');
    if (lines.length < 2) continue;
    const timeMatch = lines[0].match(/([\d:.]+)\s*-->\s*([\d:.]+)/);
    if (!timeMatch) continue;
    const start = parseTime(timeMatch[1]);
    const end = parseTime(timeMatch[2]);
    const text = lines.slice(1).join('\n').trim();
    if (text) cues.push({ start, end, text });
  }
  return cues;
}

const LANG_LABELS = {
  es: 'Español', en: 'English', fr: 'Français', pt: 'Português',
  de: 'Deutsch', ja: '日本語', ko: '한국어', zh: '中文',
};

function findSpanishTrack(tracks) {
  return tracks.find(t => {
    const l = (t.label || '').toLowerCase();
    return l.includes('español') || l.includes('espanol') || l.includes('spanish');
  });
}

function Player({ videoId, title, subtitles, posterUrl, apiUrl, episodeIndex, episodeCount, onPrev, onNext }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentText, setCurrentText] = useState('');
  const [cues, setCues] = useState([]);
  const [delay, setDelay] = useState(0);
  const [selectedSub, setSelectedSub] = useState(null);
  const [availableSubs, setAvailableSubs] = useState([]);
  const [retryCount, setRetryCount] = useState(0);
  const [savedTime, setSavedTime] = useState(null);
  const [showResume, setShowResume] = useState(false);
  const [audioTracks, setAudioTracks] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const lastSaveRef = useRef(0);

  const streamUrl = `${apiUrl}/api/videos/${videoId}/stream`;

  // ── Load saved progress ────────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PROGRESS_KEY(videoId));
      if (raw) {
        const data = JSON.parse(raw);
        if (data.time > RESUME_THRESHOLD) {
          setSavedTime(data.time);
          setShowResume(true);
        }
      }
    } catch (_) {}
  }, [videoId]);

  // ── Detect audio tracks ────────────────────────
  useEffect(() => {
    let cancelled = false;
    setAudioTracks(null);
    setSelectedAudio(null);

    fetch(`${apiUrl}/api/videos/${videoId}/audio-tracks`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (cancelled || !data.tracks || data.tracks.length <= 1) return;
        const tracks = data.tracks;
        setAudioTracks(tracks);

        // Auto-select Spanish for certain series
        const t = (title || '').toLowerCase();
        if (SPANISH_DEFAULT.some(s => t.includes(s))) {
          const spanish = findSpanishTrack(tracks);
          if (spanish) {
            setSelectedAudio(spanish.index);
            setAudioTracks(null); // skip dialog
          }
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [videoId, apiUrl, title]);

  // ── Save progress ─────────────────────────────
  const saveProgress = useCallback((time) => {
    if (!time || time < 1) return;
    const now = Date.now();
    if (now - lastSaveRef.current < 3000) return;
    lastSaveRef.current = now;
    try {
      localStorage.setItem(PROGRESS_KEY(videoId), JSON.stringify({
        time,
        title,
        timestamp: now,
      }));
    } catch (_) {}
  }, [videoId, title]);

  const clearProgress = useCallback(() => {
    try { localStorage.removeItem(PROGRESS_KEY(videoId)); } catch (_) {}
    setSavedTime(null);
    setShowResume(false);
  }, [videoId]);

  const handleResume = useCallback(() => {
    if (videoRef.current && savedTime) {
      videoRef.current.currentTime = savedTime;
    }
    setShowResume(false);
  }, [savedTime]);

  const handleRestart = useCallback(() => {
    clearProgress();
  }, [clearProgress]);

  // ── Audio sync: native silent, external controlled by video UI ──
  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video || !audio || selectedAudio == null) return;

    let suppress = false;
    let extPlaying = true;

    video.muted = true;
    audio.volume = video.volume;

    const onVolumeChange = () => {
      if (suppress) { suppress = false; return; }
      if (!video.muted) {
        suppress = true;
        video.muted = true;
        extPlaying = !extPlaying;
        audio.muted = !extPlaying;
      }
      audio.volume = video.volume;
    };

    video.addEventListener('volumechange', onVolumeChange);

    const onPlay = () => { audio.play().catch(() => {}); };
    const onPause = () => { audio.pause(); };
    const onSeeking = () => { audio.pause(); };
    const onSeeked = () => {
      audio.currentTime = video.currentTime;
      if (!video.paused) audio.play().catch(() => {});
    };
    const onRateChange = () => { audio.playbackRate = video.playbackRate; };
    const onLoaded = () => {
      audio.volume = video.volume;
      audio.currentTime = video.currentTime;
      if (!video.paused) audio.play().catch(() => {});
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('seeking', onSeeking);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('ratechange', onRateChange);
    audio.addEventListener('loadeddata', onLoaded);

    if (!video.paused) {
      audio.currentTime = video.currentTime;
      audio.play().catch(() => {});
    }

    return () => {
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('seeking', onSeeking);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('ratechange', onRateChange);
      audio.removeEventListener('loadeddata', onLoaded);
      audio.pause();
    };
  }, [selectedAudio]);

  // ── Save on pause / unmount ───────────────────
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPause = () => {
      if (video.currentTime > 1) {
        saveProgress(video.currentTime);
        lastSaveRef.current = Date.now();
      }
    };

    video.addEventListener('pause', onPause);
    return () => {
      if (video.currentTime > 1) saveProgress(video.currentTime);
      video.removeEventListener('pause', onPause);
    };
  }, [videoId, saveProgress]);

  useEffect(() => {
    if (subtitles && subtitles.length > 0) {
      setAvailableSubs(subtitles);
      setSelectedSub(subtitles[0]);
    } else {
      setAvailableSubs([]);
      setSelectedSub(null);
      setCues([]);
    }
  }, [subtitles]);

  useEffect(() => {
    if (!selectedSub) { setCues([]); return; }
    fetch(`${apiUrl}/api/subtitles/${selectedSub.id}`, { credentials: 'include' })
      .then(r => r.text())
      .then(text => setCues(parseVTT(text)))
      .catch(() => setCues([]));
  }, [selectedSub, apiUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setError(null);
    setLoading(true);

    const onLoaded = () => setLoading(false);
    const onCanPlay = () => setLoading(false);
    const onWaiting = () => setLoading(true);
    const onPlaying = () => setLoading(false);
    const onError = () => setError('Error al cargar el video.');

    video.addEventListener('loadeddata', onLoaded);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('playing', onPlaying);
    video.addEventListener('error', onError);
    video.load();

    return () => {
      video.removeEventListener('loadeddata', onLoaded);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('error', onError);
    };
  }, [videoId, retryCount]);

  const handleRetry = () => setRetryCount(c => c + 1);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.currentTime > 1) saveProgress(video.currentTime);
    if (cues.length === 0) { setCurrentText(''); return; }
    const t = video.currentTime - delay;
    const cue = cues.find(c => t >= c.start && t < c.end);
    setCurrentText(cue ? cue.text : '');
  }, [cues, delay, saveProgress]);

  const handleDelayChange = (e) => setDelay(parseFloat(e.target.value));
  const handleSubtitleChange = (e) => {
    const sub = availableSubs.find(s => s.id === e.target.value);
    setSelectedSub(sub || null);
  };
  const langLabel = (sub) => LANG_LABELS[sub.lang] || sub.lang || sub.name;

  return (
    <div className="player-container" ref={containerRef}>
      <h2 className="player-title">{title}</h2>
      <div className="player-wrapper">
        {audioTracks && selectedAudio == null && (
          <div className="audio-select-overlay">
            <div className="audio-select-dialog">
              <p className="audio-select-title">Seleccioná el audio</p>
              <div className="audio-select-options">
                {audioTracks.map(t => (
                  <button key={t.index} className="btn-audio-option" onClick={() => setSelectedAudio(t.index)}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {showResume && (
          <div className="resume-overlay">
            <div className="resume-dialog">
              <p className="resume-title">¿Continuar reproducción?</p>
              <p className="resume-hint">Dejaste de ver en {formatTime(savedTime)}</p>
              <div className="resume-actions">
                <button onClick={handleResume} className="btn-resume-primary">Continuar</button>
                <button onClick={handleRestart} className="btn-resume-secondary">Desde el principio</button>
              </div>
            </div>
          </div>
        )}

        {error ? (
          <div className="player-error">
            <p>{error}</p>
            <button onClick={handleRetry} className="btn-retry">Reintentar</button>
          </div>
        ) : (
          <>
            {loading && (
              <div className="player-loading">
                {posterUrl && <img src={posterUrl} alt="" className="loading-poster" crossOrigin="use-credentials" />}
                <div className="loading-spinner">
                  <div className="spinner" />
                  <span>Cargando video...</span>
                </div>
              </div>
            )}
            {(selectedAudio != null || !audioTracks) && (
              <video
                key={`${videoId}-${retryCount}`}
                ref={videoRef}
                controls autoPlay playsInline preload="auto"
                crossOrigin="use-credentials"
                onTimeUpdate={handleTimeUpdate}
                className="video-element"
                style={{ display: loading ? 'none' : 'block' }}
              >
                <source src={streamUrl} type="video/mp4" />
              </video>
            )}
          </>
        )}
        {currentText && (
          <div className="subtitle-overlay">
            <span className="subtitle-text">{currentText}</span>
          </div>
        )}
      </div>

      {(onPrev || onNext) && (
        <div className="episode-nav">
          <button onClick={onPrev} disabled={!onPrev} className="btn-episode-nav">&larr; Anterior</button>
          <span className="episode-counter">{episodeIndex >= 0 ? `${episodeIndex + 1} de ${episodeCount}` : ''}</span>
          <button onClick={onNext} disabled={!onNext} className="btn-episode-nav">Siguiente &rarr;</button>
        </div>
      )}

      {availableSubs.length > 0 && (
        <div className="subtitle-controls">
          <div className="subtitle-control">
            <label>Subtítulos:</label>
            <select value={selectedSub?.id || ''} onChange={handleSubtitleChange}>
              <option value="">Desactivados</option>
              {availableSubs.map(s => (
                <option key={s.id} value={s.id}>{langLabel(s)}</option>
              ))}
            </select>
          </div>
          {selectedSub && (
            <div className="subtitle-control">
              <label>Sincronización: {delay > 0 ? '+' : ''}{delay.toFixed(2)}s</label>
              <input type="range" min="-60" max="60" step="0.05" value={delay} onChange={handleDelayChange} className="delay-slider" />
            </div>
          )}
        </div>
      )}

      {selectedAudio != null && (
        <audio key={selectedAudio} ref={audioRef} src={`${apiUrl}/api/videos/${videoId}/audio/${selectedAudio}/stream`} preload="auto" crossOrigin="use-credentials" />
      )}
    </div>
  );
}

export default Player;
